/**
 * ForgeCoachPage — /forge-coach
 *
 * The persistent AI career coach (Pro+). Streams a phase-aware conversation,
 * keeps a live Career Dossier, and routes the subscriber through the 8-phase arc.
 * Backed by /api/v1/coach (Backbone v0 agent).
 */
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import PsychologyIcon from "@mui/icons-material/Psychology";
import LoginIcon from "@mui/icons-material/Login";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import { useCoachChat } from "../hooks/useCoachChat";
import PhaseIndicator from "../components/coach/PhaseIndicator";
import DossierRail from "../components/coach/DossierRail";
import ChatPane from "../components/coach/ChatPane";

export default function ForgeCoachPage() {
  const { t } = useTranslation("pages-products");
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const tierAccess = getTierAccess(user);
  const hasAccess = tierAccess.canAccessCoach;

  // Logged-out visitors get an explicit, discoverable sign-in gate (D-28) —
  // not a silent <Navigate to="/login">. The sign-in CTA preserves the return
  // path via router `state.from`, which LoginPage reads to bounce back here.
  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ py: 10 }}>
        <Paper
          elevation={0}
          sx={{ border: 1, borderColor: "divider", borderRadius: 3, p: 5, textAlign: "center" }}
        >
          <PsychologyIcon color="secondary" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h4" fontWeight={500} gutterBottom>
            {t("ForgeCoachPage.signInGateTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
            {t("ForgeCoachPage.signInGateDescription")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<LoginIcon />}
              onClick={() => navigate("/login", { state: { from: "/forge-coach" } })}
              sx={{ fontWeight: 500 }}
            >
              {t("ForgeCoachPage.signInButton")}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={() => navigate("/forge-coach/about")}
              sx={{ fontWeight: 500 }}
            >
              {t("ForgeCoachPage.learnMoreButton")}
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  if (!hasAccess) {
    return (
      <Container maxWidth="sm" sx={{ py: 10 }}>
        <Paper elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 3, p: 5, textAlign: "center" }}>
          <PsychologyIcon color="secondary" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h4" fontWeight={500} gutterBottom>
            {t("ForgeCoachPage.gateTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
            {t("ForgeCoachPage.gateDescription")}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<RocketLaunchIcon />}
            onClick={() => navigate("/pricing")}
            sx={{ fontWeight: 500 }}
          >
            {t("ForgeCoachPage.viewPlansButton")}
          </Button>
        </Paper>
      </Container>
    );
  }

  return <CoachWorkspace />;
}

function CoachWorkspace() {
  const { t } = useTranslation("pages-products");
  const { messages, dossier, phase, streamingText, sending, loading, error, send } =
    useCoachChat();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
        <PsychologyIcon color="secondary" />
        <Typography variant="h4" fontWeight={500}>
          {t("ForgeCoachPage.heading")}
        </Typography>
      </Stack>
      <Box mb={3} sx={{ maxWidth: 420 }}>
        <PhaseIndicator phase={phase} />
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 3,
              p: 2.5,
              height: { md: "68vh" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            {loading ? (
              <Typography color="text.secondary">{t("ForgeCoachPage.loadingMessage")}</Typography>
            ) : (
              <>
                {messages.length === 0 && !sending && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={500}>
                      {t("ForgeCoachPage.emptyStateTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {t("ForgeCoachPage.emptyStateDescription")}
                    </Typography>
                  </Box>
                )}
                <ChatPane
                  messages={messages}
                  streamingText={streamingText}
                  sending={sending}
                  onSend={send}
                />
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <DossierRail dossier={dossier} />
        </Grid>
      </Grid>
    </Container>
  );
}
